/**
 * Enemy special behaviours and death resolution (TRD-06 §4, §6).
 *
 * Death resolution runs as system 10, after every projectile has moved, precisely so a
 * kill cannot mutate the enemy array while system 8 is still walking it (TRD-05 R-05-20).
 * By the time this runs, any tombstoned enemy died on THIS tick — the previous tick's
 * dead were swept by compaction at step 13 — so the pass needs no "already resolved"
 * flag, and cannot double-award a bounty.
 */
import { TICK_RATE } from '../constants.js';
import type { Enemy, EntityPool } from '../entities.js';
import type { SimEvent } from '../events.js';
import { hasFlag, type EnemyArchetype, type EnemyTable } from '../scenario.js';
import { isStatusActive } from '../statuses.js';
import type { EntityId } from '../types.js';
import { isImmobilised } from './status.js';
import type { SpatialGrid } from './spatial.js';

/** R-06-14 — menders pulse on a fixed cadence rather than continuously. */
export const MENDER_INTERVAL_TICKS = 30;

/**
 * R-06-13 amended for B-20 — split cascades are bounded at two stages, not one.
 *
 * Raised from 1 so the Matriarch boss can split into splitters that split once more.
 * No other authored chain reaches depth 2 (splitterlings are not splitters), so every
 * pre-existing enemy behaves exactly as before.
 */
export const MAX_SPLIT_DEPTH = 2;

/** R-06-24 — a wave that would exceed this stalls its spawns rather than dropping them. */
export const MAX_LIVE_ENEMIES = 400;

/** Children are fanned out so they do not stack into a single visual blob (R-06-13). */
export const SPLIT_OFFSET_TILES = 0.3;

export interface DeathContext {
  tick: number;
  enemies: EntityPool<Enemy>;
  grid: SpatialGrid;
  table: EnemyTable;
  events: SimEvent[];
  scratch: EntityId[];
  allocateId: () => EntityId;
  /** Spawns a child at a given pathT, wired up by the world. */
  spawnChild: (archetype: EnemyArchetype, parent: Enemy, pathT: number, depth: number) => void;
  awardBounty: (amount: number) => void;
}

/**
 * Resolve everything that dying causes: bounty, the kill event, and splitting.
 *
 * Splitting is capped at depth 1 (R-06-13). Without the cap a splitter whose children are
 * also splitters produces an exponential cascade, and TRD-02 V-5 validates content
 * against the same bound so the two cannot disagree.
 */
export function resolveDeaths(ctx: DeathContext): void {
  const items = ctx.enemies.items;

  for (let i = 0; i < items.length; i++) {
    const e = items[i] as Enemy;
    if (e.alive) continue;

    const archetype = ctx.table[e.archetype];
    ctx.awardBounty(e.bounty);
    ctx.events.push({
      t: 'enemyKilled',
      enemy: e.id,
      x: e.x,
      y: e.y,
      archetype: e.archetype,
      isBoss: archetype ? hasFlag(archetype, 'boss') : false,
    });

    if (!archetype || !hasFlag(archetype, 'splitter') || !archetype.splitInto) continue;
    if (e.splitDepth >= MAX_SPLIT_DEPTH) continue;

    const child = ctx.table[archetype.splitInto.id];
    if (!child) continue;

    const count = archetype.splitInto.count;
    for (let n = 0; n < count; n++) {
      const offset = (n - (count - 1) / 2) * SPLIT_OFFSET_TILES;
      ctx.spawnChild(child, e, e.pathT + offset, e.splitDepth + 1);
    }
  }
}

/**
 * Mender healing (R-06-14).
 *
 * A mender never heals itself, and a stunned or frozen one heals nothing at all
 * (TRD-05 R-05-25) — that is the counterplay that makes crowd control worth bringing
 * against the wave-20 boss.
 */
export function tickMenders(
  enemies: EntityPool<Enemy>,
  table: EnemyTable,
  grid: SpatialGrid,
  tick: number,
  scratch: EntityId[],
): void {
  if (tick % MENDER_INTERVAL_TICKS !== 0) return;

  const items = enemies.items;
  for (let i = 0; i < items.length; i++) {
    const mender = items[i] as Enemy;
    if (!mender.alive) continue;

    const archetype = table[mender.archetype];
    if (!archetype || !hasFlag(archetype, 'mender')) continue;
    if (archetype.healRadius === undefined || archetype.healPercentPerSecond === undefined)
      continue;
    if (isImmobilised(mender, tick)) continue;

    grid.query(mender.x, mender.y, archetype.healRadius, enemies, scratch);
    const ids = scratch.slice();
    // Half a second's worth, because the pulse is every 30 ticks.
    const fraction = (archetype.healPercentPerSecond * MENDER_INTERVAL_TICKS) / TICK_RATE;

    for (let j = 0; j < ids.length; j++) {
      const id = ids[j] as EntityId;
      if (id === mender.id) continue;
      const patient = enemies.get(id);
      if (!patient) continue;
      const healed = patient.hp + patient.maxHp * fraction;
      patient.hp = healed > patient.maxHp ? patient.maxHp : healed;
    }
  }
}

/** R-06-15 — flat per-hit absorption, read by the damage pipeline at stage 7. */
export function shieldOf(archetype: EnemyArchetype | undefined): number {
  return archetype?.shieldPerHit ?? 0;
}

/** Bosses at wave 25 ignore holds entirely (R-06-16); everything else is subject to them. */
export function ignoresHolds(archetype: EnemyArchetype | undefined): boolean {
  return archetype !== undefined && hasFlag(archetype, 'boss') && hasFlag(archetype, 'flying');
}

export { isStatusActive };
